import { Card, CardContent } from "@/components/ui/card";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";

interface MetricCardProps {
  title: string;
  value: string | number;
  previousValue?: string | number;
  format?: "currency" | "number" | "percentage" | "multiplier";
  invertColors?: boolean;
}

function formatValue(val: string | number, format: string): string {
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num)) return String(val);
  switch (format) {
    case "currency": return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "percentage": return `${num.toFixed(2)}%`;
    case "multiplier": return `${num.toFixed(2)}x`;
    case "number": return num.toLocaleString();
    default: return String(val);
  }
}

export function MetricCard({ title, value, previousValue, format = "number", invertColors = false }: MetricCardProps) {
  const currentNum = typeof value === "string" ? parseFloat(value) : value;
  const prevNum = previousValue !== undefined ? (typeof previousValue === "string" ? parseFloat(previousValue) : previousValue) : null;

  let delta: number | null = null;
  let deltaStr = "";
  if (prevNum !== null && !isNaN(prevNum) && prevNum !== 0) {
    delta = ((currentNum - prevNum) / prevNum) * 100;
    deltaStr = `${delta > 0 ? "+" : ""}${delta.toFixed(1)}%`;
  }

  const isPositive = delta !== null && (invertColors ? delta < 0 : delta > 0);
  const isNegative = delta !== null && (invertColors ? delta > 0 : delta < 0);

  return (
    <Card data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-4">
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{formatValue(value, format)}</p>
        {delta !== null && (
          <div className={`flex items-center gap-1 mt-1 text-sm ${isPositive ? "text-green-600" : isNegative ? "text-red-600" : "text-gray-500"}`}>
            {isPositive ? <ArrowUp className="h-3 w-3" /> : isNegative ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            <span>{deltaStr}</span>
            <span className="text-gray-400 ml-1">vs prev</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
